/**
 * AccountActivation Component
 * 
 * Handles account activation when users click the activation link from their email.
 * Activates the user account and redirects to login page.
 * 
 * @returns {JSX.Element} Account activation page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { activateAccount, resendActivation } from '../../services/api';

const AccountActivation = () => {
  const [activationStatus, setActivationStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | error
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const activateUserAccount = async () => {
      try {
        // Extract token from URL parameters
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');

        if (!token) {
          setActivationStatus('error');
          setMessage('Invalid activation link. Token parameter is missing or malformed.');
          return;
        }

        // Call activation API with token
  const response = await activateAccount(token);
        
        if (response.success) {
          setActivationStatus('success');
          setMessage('Your account has been successfully activated! You can now log in.');
        } else {
          setActivationStatus('error');
          setMessage(response.message || 'Account activation failed. Please try again.');
        }
      } catch (error) {
        console.error('Activation error:', error);
        setActivationStatus('error');
        setMessage(
          error.response?.data?.message || 
          'An error occurred during account activation. Please try again or contact support.'
        );
      }
    };

    activateUserAccount();
  }, [location.search]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToHome = () => {
    navigate('/');
  };

  const handleResend = async () => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email'); // Only available if original link also had email (optional)
    if (!email) {
      setResendStatus('error');
      setMessage('Cannot resend activation: email not provided in URL.');
      return;
    }
    setResendStatus('sending');
    try {
      const res = await resendActivation(email);
      setResendStatus('sent');
      setMessage(res.message || 'If the account exists, an activation email has been sent.');
    } catch (err) {
      setResendStatus('error');
      setMessage(err.response?.data?.message || 'Failed to resend activation email.');
    }
  };

  const renderContent = () => {
    switch (activationStatus) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="mb-4">
              <div className="spinner-border text-conference-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
            <h4 className="text-conference-primary mb-3">Activating Your Account</h4>
            <p className="text-muted">Please wait while we activate your account...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mb-4">
              <i
                className="bi bi-check-circle-fill text-success"
                style={{ fontSize: '4rem' }}
              ></i>
            </div>
            <h4 className="text-success mb-3">Account Activated Successfully!</h4>
            <p className="lead mb-4">{message}</p>

            <div className="alert alert-success" role="alert">
              <i className="bi bi-info-circle me-2"></i>
              Your account is now active and ready to use. You can log in with your credentials.
            </div>

            <div className="mt-4">
              <button
                type="button"
                className="btn btn-conference-primary me-3"
                onClick={handleGoToLogin}
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Go to Login
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleGoToHome}
              >
                <i className="bi bi-house me-2"></i>
                Back to Home
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="mb-4">
              <i
                className="bi bi-exclamation-triangle-fill text-danger"
                style={{ fontSize: '4rem' }}
              ></i>
            </div>
            <h4 className="text-danger mb-3">Activation Failed</h4>
            <p className="lead mb-4">{message}</p>

            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              If you continue to experience issues, please contact our support team.
            </div>

            <div className="mt-4 d-flex flex-wrap justify-content-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleGoToHome}
              >
                <i className="bi bi-house me-2"></i>
                Home
              </button>
              <button
                type="button"
                className="btn btn-outline-warning"
                disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                onClick={handleResend}
              >
                <i className="bi bi-arrow-repeat me-2"></i>
                {resendStatus === 'sending' ? 'Resending...' : resendStatus === 'sent' ? 'Email Sent' : 'Resend Activation'}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => window.location.href = 'mailto:mfc@mfsociety.org'}
              >
                <i className="bi bi-envelope me-2"></i>
                Contact Support
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card card-conference shadow">
            <div className="card-header bg-conference-gradient text-white text-center">
              <h2 className="mb-0 text-white">
                <i className="bi bi-shield-check me-2"></i>
                Account Activation
              </h2>
            </div>

            <div className="card-body p-4">
              {renderContent()}

              {/* Support Information */}
              <div className="mt-5 pt-4 border-top">
                <h6 className="text-muted mb-2">Need Help?</h6>
                <p className="text-muted small mb-0">
                  If you need assistance, please contact us at{' '}
                  <a 
                    href="mailto:mfc@mfsociety.org" 
                    className="text-conference-primary text-decoration-none"
                  >
                    mfc@mfsociety.org
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountActivation;
